import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, StatCard, StatusPill } from "../components/shared/Primitives";
import { Button } from "../components/ui/button";
import EntryModal from "../components/shared/EntryModal";
import ExcelImportDialog from "../components/shared/ExcelImportDialog";
import { Plus, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { downloadCSV } from "../lib/csv";
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  LineChart,
  Line,
} from "recharts";

const DEFAULT_CATEGORIES = ["General", "Food", "Travel", "Mileage", "Software", "Subscriptions", "Office", "Utilities", "Personal"];
// 2025 IRS standard business mileage rate; users can override per entry.
const DEFAULT_MILEAGE_RATE = 0.70;
const COLORS = ["#0F1115", "#0090FF", "#46A758", "#FFC53D", "#F76B15", "#E54D2E", "#8E4EC6", "#0E7AB2"];

export default function Expenses() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState({ open: false, entry: null });
  const [importOpen, setImportOpen] = useState(false);
  const [period, setPeriod] = useState("month");
  const [refIso] = useState(new Date().toISOString().slice(0, 7));

  const load = async () => {
    const { data } = await api.get("/expenses");
    setItems(data);
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const today = new Date();
    return items.filter((e) => {
      if (!e.date) return false;
      const d = new Date(e.date + "T12:00:00");
      if (period === "week") {
        const diff = (today - d) / (1000 * 60 * 60 * 24);
        return diff <= 7 && diff >= -7;
      }
      if (period === "month") return e.date.slice(0, 7) === refIso;
      if (period === "quarter") {
        const q1 = Math.floor(today.getMonth() / 3);
        const q2 = Math.floor(d.getMonth() / 3);
        return q1 === q2 && today.getFullYear() === d.getFullYear();
      }
      if (period === "year") return d.getFullYear() === today.getFullYear();
      return true;
    });
  }, [items, period, refIso]);

  const total = filtered.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const truthy = (v) => v === true || v === "true" || v === 1;
  const upcomingBills = items.filter((e) => ["pending", "due"].includes((e.status || "").toLowerCase()));
  const recurring = items.filter((e) => truthy(e.recurring));
  const reimbursable = items.filter((e) => truthy(e.reimbursable) && !truthy(e.reimbursed));
  const overdue = items.filter((e) => {
    if (!e.date) return false;
    return new Date(e.date) < new Date() && !["paid", "reimbursed"].includes((e.status || "").toLowerCase());
  });

  // Mileage rollup — sum of miles logged inside the current view period.
  const mileageMiles = filtered
    .filter((e) => (e.category || "").toLowerCase() === "mileage")
    .reduce((s, e) => s + (parseFloat(e.miles) || 0), 0);

  const byCat = useMemo(() => {
    const m = {};
    for (const e of filtered) {
      const k = e.category || "General";
      m[k] = (m[k] || 0) + (parseFloat(e.amount) || 0);
    }
    return Object.entries(m).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  // Monthly spending trend (last 12 months)
  const monthlyTrend = useMemo(() => {
    const today = new Date();
    const months = [];
    for (let i = 11; i >= 0; i--) {
      const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      months.push({ month: key, total: 0 });
    }
    const lookup = Object.fromEntries(months.map((m, i) => [m.month, i]));
    for (const e of items) {
      const m = (e.date || "").slice(0, 7);
      if (m in lookup) months[lookup[m]].total += parseFloat(e.amount) || 0;
    }
    return months.map((m) => ({ month: m.month.slice(5), total: Math.round(m.total) }));
  }, [items]);

  // By payment method
  const byMethod = useMemo(() => {
    const m = {};
    for (const e of filtered) {
      const k = e.payment_method || "Other";
      m[k] = (m[k] || 0) + (parseFloat(e.amount) || 0);
    }
    return Object.entries(m).map(([name, value]) => ({ name, value: Math.round(value) }));
  }, [filtered]);

  // By status
  const byStatus = useMemo(() => {
    const m = {};
    for (const e of filtered) {
      const k = e.status || "Pending";
      m[k] = (m[k] || 0) + 1;
    }
    return Object.entries(m).map(([name, count]) => ({ name, count }));
  }, [filtered]);

  const save = async (p) => {
    // Mileage auto-calc: if category=='Mileage' and miles provided, amount = miles * rate.
    if ((p.category || "").toLowerCase() === "mileage") {
      const miles = parseFloat(p.miles);
      const rate = parseFloat(p.mileage_rate) || DEFAULT_MILEAGE_RATE;
      if (!isNaN(miles) && miles > 0) {
        p.amount = Number((miles * rate).toFixed(2));
        p.mileage_rate = rate;
        if (!p.vendor) p.vendor = "Mileage";
      }
    }
    if (p.id) await api.put(`/expenses/${p.id}`, p);
    else await api.post(`/expenses`, p);
    toast.success("Expense saved");
    load();
  };
  const del = async () => {
    if (modal.entry?.id) {
      await api.delete(`/expenses/${modal.entry.id}`);
      toast.success("Expense deleted");
      load();
    }
  };
  const duplicate = async (e) => {
    const copy = { ...e, id: undefined };
    await api.post("/expenses", copy);
    toast.success("Duplicated");
    load();
  };

  const exportCSV = () => {
    downloadCSV(
      `expenses-${refIso}.csv`,
      filtered,
      [
        { key: "date", label: "Date" },
        { key: "vendor", label: "Vendor" },
        { key: "category", label: "Category" },
        { key: "amount", label: "Amount" },
        { key: "payment_method", label: "Payment Method" },
        { key: "status", label: "Status" },
        { key: "notes", label: "Notes" },
      ]
    );
  };

  return (
    <div>
      <PageHeader
        eyebrow="Personal Finance"
        title="Expense Management"
        description="Track personal spend, bills, subscriptions and reimbursements."
        right={
          <>
            <Button variant="outline" onClick={exportCSV} data-testid="exp-export-csv">
              <Download size={14} className="mr-2" /> Export CSV
            </Button>
            <Button variant="outline" onClick={() => setImportOpen(true)} data-testid="exp-import-btn">
              <Upload size={14} className="mr-2" /> Import Excel
            </Button>
            <Button onClick={() => setModal({ open: true, entry: null })} className="bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)]" data-testid="add-expense-btn">
              <Plus size={14} className="mr-2" /> New Expense
            </Button>
          </>
        }
      />
      <div className="p-8 space-y-6">
        <div className="flex items-center gap-2">
          <span className="label-eyebrow mr-2">View</span>
          {["week", "month", "quarter", "year"].map((p) => (
            <Button
              key={p}
              size="sm"
              variant={period === p ? "default" : "outline"}
              className={period === p ? "bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)] capitalize" : "capitalize"}
              onClick={() => setPeriod(p)}
              data-testid={`exp-period-${p}`}
            >
              {p}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <StatCard label="Total" value={`$${total.toLocaleString(undefined, { maximumFractionDigits: 0 })}`} testid="exp-total" />
          <StatCard label="Miles logged" value={mileageMiles.toLocaleString(undefined, { maximumFractionDigits: 1 })} testid="exp-miles" />
          <StatCard label="Upcoming Bills" value={upcomingBills.length} />
          <StatCard label="Recurring" value={recurring.length} />
          <StatCard label="Reimbursable" value={reimbursable.length} />
          <StatCard label="Overdue" value={overdue.length} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-[var(--rw-border)] rounded-[4px] p-5">
            <div className="label-eyebrow mb-3">Monthly spending · last 12 months</div>
            <div className="h-60">
              {monthlyTrend.every((m) => m.total === 0) ? (
                <div className="h-full grid place-items-center text-sm text-neutral-400">No spending yet.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={monthlyTrend}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v / 1000}k`} />
                    <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Line type="monotone" dataKey="total" stroke="#0F1115" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-white border border-[var(--rw-border)] rounded-[4px] p-5">
            <div className="label-eyebrow mb-3">Spending by category</div>
            <div className="h-60">
              {byCat.length === 0 ? (
                <div className="h-full grid place-items-center text-sm text-neutral-400">No data.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={byCat} dataKey="value" innerRadius={40} outerRadius={75} paddingAngle={1}>
                      {byCat.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Legend iconType="square" wrapperStyle={{ fontSize: 10 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white border border-[var(--rw-border)] rounded-[4px] p-5">
            <div className="label-eyebrow mb-3">By payment method</div>
            <div className="h-56">
              {byMethod.length === 0 ? (
                <div className="h-full grid place-items-center text-sm text-neutral-400">No data.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byMethod}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                    <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} />
                    <Bar dataKey="value" fill="#0090FF" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="bg-white border border-[var(--rw-border)] rounded-[4px] p-5">
            <div className="label-eyebrow mb-3">Status breakdown</div>
            <div className="h-56">
              {byStatus.length === 0 ? (
                <div className="h-full grid place-items-center text-sm text-neutral-400">No data.</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byStatus} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#46A758" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-[var(--rw-border)] rounded-[4px] overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[var(--rw-surface-2)]">
              <tr className="text-left text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-500">
                <th className="py-2 px-3">Date</th>
                <th className="py-2 px-3">Vendor</th>
                <th className="py-2 px-3">Category</th>
                <th className="py-2 px-3 text-right">Amount</th>
                <th className="py-2 px-3">Method</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Receipt</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-8 text-neutral-400">No expenses for this period.</td>
                </tr>
              )}
              {filtered.map((e) => (
                <tr key={e.id} className="border-t border-[var(--rw-border)] hover:bg-[var(--rw-surface-2)] cursor-pointer" onClick={() => setModal({ open: true, entry: e })} data-testid={`exp-row-${e.id}`}>
                  <td className="py-2 px-3 font-mono-rw text-xs">{e.date}</td>
                  <td className="py-2 px-3 font-semibold">
                    {e.vendor}
                    {(e.category || "").toLowerCase() === "mileage" && e.miles ? (
                      <div className="text-[10px] font-normal text-neutral-500 mt-0.5">
                        {parseFloat(e.miles).toLocaleString(undefined, { maximumFractionDigits: 1 })} mi @ ${(parseFloat(e.mileage_rate) || DEFAULT_MILEAGE_RATE).toFixed(2)}/mi
                      </div>
                    ) : null}
                  </td>
                  <td className="py-2 px-3">
                    <span className="text-[10px] uppercase tracking-widest bg-neutral-100 border border-neutral-200 px-1.5 py-0.5 rounded">
                      {e.category || "General"}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right font-mono-rw tabular-nums">${Number(e.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  <td className="py-2 px-3 text-neutral-500 text-xs">{e.payment_method}</td>
                  <td className="py-2 px-3"><StatusPill value={e.status} /></td>
                  <td className="py-2 px-3 text-xs">{e.receipt_id ? "📎" : "—"}</td>
                  <td className="py-2 px-3 text-right">
                    <button onClick={(ev) => { ev.stopPropagation(); duplicate(e); }} className="text-xs text-neutral-400 hover:text-neutral-700" data-testid={`exp-dup-${e.id}`}>
                      Duplicate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <EntryModal
        open={modal.open}
        onOpenChange={(v) => setModal({ ...modal, open: v })}
        title={modal.entry ? "Edit Expense" : "New Expense"}
        entry={modal.entry}
        promptAddContact={false}
        fields={[
          { name: "date", label: "Date", type: "date" },
          { name: "vendor", label: "Vendor / Payee" },
          { name: "category", label: "Category", type: "select", options: DEFAULT_CATEGORIES },
          { name: "amount", label: "Amount", type: "number" },
          { name: "miles", label: "Miles (Mileage only)", type: "number" },
          { name: "mileage_rate", label: `Rate $ / mi (default ${DEFAULT_MILEAGE_RATE})`, type: "number" },
          { name: "payment_method", label: "Payment Method", type: "select", options: ["Card", "Cash", "Bank Transfer", "Check", "Other"] },
          { name: "status", label: "Status", type: "select", options: ["Pending", "Paid", "Due", "Reimbursed", "Overdue"] },
          { name: "recurring", label: "Recurring?", type: "select", options: ["false", "true"] },
          { name: "reimbursable", label: "Reimbursable?", type: "select", options: ["false", "true"] },
          { name: "notes", label: "Notes", type: "textarea", full: true },
          { name: "receipt_id", label: "Receipt (upload)", type: "file", accept: "image/*,application/pdf", full: true },
        ]}
        onSave={save}
        onDelete={del}
      />

      <ExcelImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        endpoint="/expenses/import-excel"
        columns={["date", "vendor", "category", "amount", "payment method", "status", "notes"]}
        onDone={load}
      />
    </div>
  );
}
